import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Activity,
  Settings,
  CheckCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  Heart,
  Video,
  MapPin,
  MessageCircle,
  TrendingUp,
  BookOpen,
  Users,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { fetchClientMessages, fetchRecommendation, fetchSessions, fetchUserPackages } from '../lib/instantData'
import { brandAlert } from '../contexts/DialogContext'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'

interface UpcomingSession {
  id: string
  title: string
  type: 'online' | 'in-person'
  date: string
  time: string
  trainer: string
  status: string

  client_join_url?: string
  admin_joined?: boolean // New field to track if admin has joined the call
  client_user_id?: string // New field to store the selected user's ID
  client_full_name?: string // New field to store the selected user's full name
  client_email?: string // New field to store the selected user's email
}

interface RecentTrainingSession {
  id: string
  title: string
  type: 'online' | 'in-person'
  date: string
  time: string
  status: 'completed' | 'cancelled' | 'no-show'
  trainer: string
}

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isNewUser, setIsNewUser] = useState(false)
  const [unreadCassieMessages, setUnreadCassieMessages] = useState(0)

  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([])
  const [recentTrainingSessions, setRecentTrainingSessions] = useState<RecentTrainingSession[]>([])
  // "This Week From Cassie" — only ever shows a note Cassie actually wrote
  // after reviewing a check-in. No placeholder/default text, ever.
  const [weeklyFocus, setWeeklyFocus] = useState<string>('')
  const [weeklyFocusUpdatedAt, setWeeklyFocusUpdatedAt] = useState<string | null>(null)

  // Progress Tracker — this client's private Google Sheet link, if Cassie
  // has set one on their active package.
  const [trackerUrl, setTrackerUrl] = useState<string | null>(null)

  // New-user welcome state — Instant auth users don't expose created_at on the client user object
  useEffect(() => {
    if (user?.id) {
      setIsNewUser(false)
      fetchUserSessions()
    }
  }, [user])

  // Poll for new messages from Cassie
  useEffect(() => {
    if (!user?.id) return
    const checkMessages = () => {
      fetchClientMessages(user.id)
        .then((rows) => {
          setUnreadCassieMessages(
            rows.filter((m) => m.senderRole === 'coach' && !m.readByClient).length
          )
        })
        .catch(() => {})
    }
    checkMessages()
    const interval = setInterval(checkMessages, 15000)
    return () => clearInterval(interval)
  }, [user])

  // "This Week From Cassie" — show the real note if one exists, nothing if not.
  useEffect(() => {
    const loadWeeklyFocus = async () => {
      if (!user?.id) return
      try {
        const data = await fetchRecommendation(user.id)
        setWeeklyFocus(data?.personal_note || '')
        setWeeklyFocusUpdatedAt(data?.updated_at || null)
      } catch (error) {
        console.log('No weekly focus note set yet')
        setWeeklyFocus('')
        setWeeklyFocusUpdatedAt(null)
      }
    }

    loadWeeklyFocus()
  }, [user])

  // Progress Tracker link — pulled from the client's active package.
  useEffect(() => {
    const loadTracker = async () => {
      if (!user?.id) return
      try {
        const packages = await fetchUserPackages(user.id)
        const active = packages.find((p) => p.status === 'active' && p.tracker_url) || null
        setTrackerUrl(active?.tracker_url || null)
      } catch (error) {
        console.log('No tracker found for this client yet')
        setTrackerUrl(null)
      }
    }

    loadTracker()
  }, [user])

  // Poll for session updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.id) {
        fetchUserSessions()
      }
    }, 10000) // Check every 10 seconds

    return () => clearInterval(interval)
  }, [user])

  // Listen for session end notifications
  useEffect(() => {
    if (user?.id) {
      // Check if any active sessions have been ended
      const checkSessionEnd = () => {
        upcomingSessions.forEach(session => {
          if (session.admin_joined === true) {
            // Check if session was ended
            fetchSessions({ userId: user.id })
              .then((rows) => {
                const data = rows.find((r) => r.id === session.id)
                if (data?.status === 'completed') {
                  void brandAlert({ title: 'Notice', message: 'Session ended by trainer.', variant: 'info' })
                  fetchUserSessions()
                }
              })
              .catch((err: any) => console.log('Error checking session status:', err))
          }
        });
      };

      // Check every 5 seconds for session end
      const sessionEndInterval = setInterval(checkSessionEnd, 5000);
      return () => clearInterval(sessionEndInterval);
    }
  }, [user, upcomingSessions])

  const fetchUserSessions = async () => {
    if (!user?.id) return

    try {
      const all = await fetchSessions({ userId: user.id })
      const upcomingData = all
        .filter((s) => s.status === 'scheduled')
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))

      setUpcomingSessions(
        upcomingData.map((session) => ({
          id: session.id,
          title: `${session.session_type === 'online' ? 'Online' : 'Private'} Training Session`,
          type: session.session_type === 'online' ? 'online' : 'in-person',
          date: new Date(session.scheduled_date).toLocaleDateString(),
          time: new Date(session.scheduled_date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          trainer: 'Cassie',
          status: session.status,
          client_join_url: session.client_join_url,
          admin_joined: session.admin_joined || false,
          client_user_id: session.user_id,
          client_full_name: session.client_full_name,
          client_email: session.client_email,
        }))
      )

      const recentData = all
        .filter((s) => s.status === 'completed' || s.status === 'cancelled')
        .slice(0, 5)

      setRecentTrainingSessions(
        recentData.map((session) => ({
          id: session.id,
          title: `${session.session_type === 'online' ? 'Online' : 'Private'} Training Session`,
          type: session.session_type === 'online' ? 'online' : 'in-person',
          date: new Date(session.scheduled_date).toLocaleDateString(),
          time: new Date(session.scheduled_date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          status: session.status as 'completed' | 'cancelled' | 'no-show',
          trainer: 'Cassie',
        }))
      )
    } catch (error) {
      console.error('Error fetching user sessions:', error)
    }
  }


  const getSessionStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'cancelled':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'no-show':
        return <AlertCircle className="w-4 h-4 text-orange-500" />
      default:
        return null
    }
  }

  const renderEmptyState = (title: string, description: string, actionText?: string, onAction?: () => void) => (
    <div className="text-center py-10">
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
        <Calendar className="w-8 h-8 text-gold" />
      </div>
      <h3 className="font-display text-xl font-semibold text-ink mb-2">{title}</h3>
      <p className="text-gray-600 mb-5 max-w-sm mx-auto">{description}</p>
      {actionText && onAction && (
        <GradientButton onClick={onAction} className="mx-auto" icon={Plus}>
          {actionText}
        </GradientButton>
      )}
    </div>
  )

  const displayName = user?.user_metadata?.full_name || 'there'
  const nextSession = upcomingSessions[0]

  return (
    <>
      <PanelShell
        eyebrow="Your dashboard"
        headerVariant="teal"
        title={isNewUser ? `Welcome, ${displayName}` : `Welcome back, ${displayName}`}
        subtitle={
          isNewUser
            ? "Let's get your training plan started."
            : 'Your coaching, progress, and community — all in one place.'
        }
        actions={
          <>
            <div className="relative">
              <GradientButton
                onClick={() => navigate('/dashboard/messages')}
                icon={MessageCircle}
                size="sm"
                variant="outline"
                className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20 hover:!border-white/50 hover:!text-white"
              >
                Message Cassie
              </GradientButton>
              {unreadCassieMessages > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center text-[10px] font-semibold bg-red-500 text-white rounded-full border-2 border-white">
                  {unreadCassieMessages}
                </span>
              )}
            </div>
            <GradientButton
              onClick={() => navigate('/dashboard/settings')}
              icon={Settings}
              size="sm"
              variant="outline"
              className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20 hover:!border-white/50 hover:!text-white"
            >
              Settings
            </GradientButton>
            {nextSession ? (
              <GradientButton
                onClick={() => navigate('/dashboard/sessions')}
                icon={ArrowRight}
                size="sm"
              >
                View next session
              </GradientButton>
            ) : (
              <GradientButton
                onClick={() => navigate('/dashboard/request-check-in')}
                icon={Plus}
                size="sm"
              >
                Request a Coaching Check-In
              </GradientButton>
            )}
          </>
        }
      >
        <div className="rounded-3xl overflow-hidden bg-brand-navy mb-6 lg:mb-8 grid grid-cols-1 sm:grid-cols-5">
          <div className="sm:col-span-3 px-6 py-7 sm:px-10 sm:py-9 flex flex-col justify-center gap-5">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-3">
                <Users className="w-4 h-4" />
                Your Fit by Cassie Community
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-2">
                Stronger together.
              </h2>
              <p className="text-sm sm:text-base text-brand-cream/80 max-w-xl">
                Connect with women who get it. Share your wins, join the conversation, and find encouragement
                along the way.
              </p>
            </div>
            <GradientButton
              onClick={() => navigate('/dashboard/community')}
              icon={ArrowRight}
              className="shrink-0 self-start !bg-none !bg-brand-blush !text-brand-navy hover:!bg-none hover:!bg-brand-blush/90 hover:!text-brand-navy"
            >
              Enter Community Center
            </GradientButton>
          </div>
          <div className="sm:col-span-2 flex items-center justify-center aspect-[3/2] sm:aspect-auto sm:h-full">
            <img
              src="/images/community/community-hero.jpg"
              alt="Four women smiling together after a workout"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <GlassCard hover={false}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-brand" />
                  Upcoming Sessions
                </h2>
                <div className="flex items-center gap-3">
                  <button onClick={fetchUserSessions} className="panel-link" title="Refresh sessions">
                    Refresh
                  </button>
                  <button onClick={() => navigate('/dashboard/sessions')} className="panel-link">
                    View all
                  </button>
                </div>
              </div>

              {upcomingSessions.length === 0 ? (
                renderEmptyState(
                  'No upcoming sessions',
                  isNewUser
                    ? 'Schedule your first training session to get started.'
                    : "You don't have any sessions scheduled yet.",
                  'Explore training',
                  () => navigate('/online-training')
                )
              ) : (
                <div className="space-y-3">
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="p-4 rounded-2xl bg-cream border border-gray-100 hover:border-gold/40 hover:shadow-soft transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {session.type === 'online' ? (
                              <Video className="w-4 h-4 text-brand" />
                            ) : (
                              <MapPin className="w-4 h-4 text-gold" />
                            )}
                            <h3 className="font-semibold text-ink truncate">{session.title}</h3>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {session.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.time}
                            </span>
                            <span
                              className={
                                session.type === 'online' ? 'panel-chip-brand' : 'panel-chip-gold'
                              }
                            >
                              {session.type === 'online' ? 'Online' : 'Private'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            {isNewUser ? (
              <GlassCard hover={false}>
                <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2 mb-6">
                  <Heart className="w-5 h-5 text-gold" />
                  Getting started
                </h2>
                <div className="space-y-4">
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-gold-soft to-cream border border-gold/20">
                    <h3 className="text-ink font-semibold mb-3">Your first steps</h3>
                    <div className="space-y-3 text-sm">
                      {[
                        'Schedule your first session to start personalized training',
                        'Complete your profile so Cassie can tailor your plan',
                        'Set clear fitness goals to track progress together',
                        'Explore training packages that fit your lifestyle',
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-brand rounded-full mt-2 flex-shrink-0" />
                          <p className="text-gray-700">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl bg-ink text-white border border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-4 h-4 text-gold-light" />
                      <span className="font-medium text-gold-light">Welcome note</span>
                    </div>
                    <p className="text-gray-200 text-sm italic leading-relaxed">
                      "Welcome to Fit by Cassie! I'm excited to be part of your journey. Every
                      expert was once a beginner — let's build habits that last."
                    </p>
                  </div>
                </div>
              </GlassCard>
            ) : weeklyFocus ? (
              <GlassCard hover={false}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
                    <Heart className="w-5 h-5 text-gold" />
                    This Week From Cassie
                  </h2>
                  {weeklyFocusUpdatedAt && (
                    <span className="panel-chip-muted">
                      Updated{' '}
                      {new Date(weeklyFocusUpdatedAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
                <div className="p-5 rounded-2xl bg-ink text-white border border-white/10">
                  <p className="text-gray-200 text-sm italic leading-relaxed">"{weeklyFocus}"</p>
                </div>
              </GlassCard>
            ) : null}

            {trackerUrl && (
              <GlassCard hover={false} className={isNewUser || weeklyFocus ? 'mt-6' : undefined}>
                <h2 className="font-display text-xl font-semibold text-ink mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-brand" />
                  Your Progress Tracker
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Your training, nutrition, check-ins and progress live in your individual Fit by
                  Cassie tracker. Use it for the tracking and check-ins included in your coaching
                  plan; I update your coaching sections.
                </p>
                <GradientButton
                  onClick={() => window.open(trackerUrl, '_blank', 'noopener,noreferrer')}
                  variant="outline"
                  size="sm"
                  icon={ArrowRight}
                >
                  Open My Progress Tracker
                </GradientButton>
              </GlassCard>
            )}
          </div>

          <div>
            <GlassCard hover={false}>
              <h2 className="font-display text-xl font-semibold text-ink mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-brand" />
                Recent sessions
              </h2>

              {recentTrainingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
                    <Activity className="w-7 h-7 text-brand" />
                  </div>
                  <h3 className="font-semibold text-ink mb-2">No recent sessions</h3>
                  <p className="text-gray-600 text-sm">
                    Completed workouts will show up here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentTrainingSessions.map((session) => (
                    <div key={session.id} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex-shrink-0 mt-1 w-8 h-8 flex items-center justify-center">
                        {session.type === 'online' ? (
                          <Video className="w-4 h-4 text-brand" />
                        ) : (
                          <MapPin className="w-4 h-4 text-gold" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{session.title}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span>{session.date}</span>
                          <span>•</span>
                          <span>{session.time}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">{getSessionStatusIcon(session.status)}</div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            <div className="mt-6">
              <GlassCard hover={false}>
                <h2 className="font-display text-xl font-semibold text-ink mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-brand" />
                  My Progress
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Submit your weekly check-in and see Cassie's feedback.
                </p>
                <GradientButton
                  onClick={() => navigate('/dashboard/progress')}
                  variant="outline"
                  size="sm"
                  icon={ArrowRight}
                >
                  View my progress
                </GradientButton>
              </GlassCard>
            </div>

            <div className="mt-6 bg-brand text-white rounded-3xl shadow-soft border border-white/10 p-5 sm:p-6">
              <h2 className="font-display text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-white/90" />
                My Resources
              </h2>
              <p className="text-sm text-white/80 mb-4">
                Your Welcome Guide, intake form, questionnaire, and waiver — all in one place.
              </p>
              <GradientButton
                onClick={() => navigate('/dashboard/client-guide')}
                variant="outline"
                size="sm"
                icon={ArrowRight}
                className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20 hover:!border-white/50 hover:!text-white"
              >
                View my documents
              </GradientButton>
            </div>

          </div>
        </div>
      </PanelShell>
    </>
  )
}

export default Dashboard
